import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare, Send, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const LOG_CLS = { sent: "bg-emerald-100 text-emerald-700", queued: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" };

export default function SmsSettings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/twilio").then(({ data }) => { setCfg(data); setF({ account_sid: data.account_sid || "", auth_token: "", from_number: data.from_number || "" }); }).catch(() => setCfg(false));
  const loadLogs = () => api.get("/settings/twilio/log").then(({ data }) => setLogs(data)).catch(() => {});
  useEffect(() => { load(); loadLogs(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { account_sid: f.account_sid, from_number: f.from_number };
      if (f.auth_token.trim()) body.auth_token = f.auth_token.trim();
      const { data } = await api.put("/settings/twilio", body);
      toast.success(data.flushed ? `Saved. Queue: ${data.flushed.sent} sent, ${data.flushed.failed} failed.` : "Settings saved");
      load(); loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a phone number (E.164, e.g. +15555550123)");
    setBusy(true);
    try { const { data } = await api.post("/settings/twilio/test", { to: testTo });
      toast[data.status === "sent" ? "success" : data.status === "queued" ? "message" : "error"](`SMS ${data.status}${data.error ? ": " + data.error : ""}`); loadLogs();
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  if (cfg === false) return <DashboardLayout nav={getNav(user.role)} title="SMS Settings"><p>Unable to load.</p></DashboardLayout>;
  const connected = cfg?.connected;

  return (
    <DashboardLayout nav={getNav(user.role)} title="SMS Settings (Twilio)">
      {!cfg ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-2xl space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between">
            <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-blue-600" /><h2 className="font-head font-semibold text-slate-900">Twilio SMS</h2></div>
            <span data-testid="sms-status-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{connected ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{connected ? "Connected" : "Not Connected"} · {cfg.queued_count} queued</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div><Label>Account SID</Label><Input data-testid="twilio-sid" value={f.account_sid} onChange={(e) => setF({ ...f, account_sid: e.target.value })} className="mt-1.5" placeholder="AC..." /></div>
            <div><Label>Auth Token</Label><PasswordInput data-testid="twilio-token" value={f.auth_token} onChange={(e) => setF({ ...f, auth_token: e.target.value })} className="mt-1.5" placeholder={cfg.masked_token || "Your Twilio auth token"} /><p className="mt-1 text-xs text-slate-400">Stored on the backend; never shown after saving ({cfg.masked_token || "not set"}).</p></div>
            <div><Label>Sending Phone Number</Label><Input data-testid="twilio-from" value={f.from_number} onChange={(e) => setF({ ...f, from_number: e.target.value })} className="mt-1.5" placeholder="+15555550123" /></div>
            <Button data-testid="twilio-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]"><Label>Test SMS Recipient</Label><Input data-testid="twilio-test-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="mt-1.5" placeholder="+15555550123" /></div>
              <Button data-testid="twilio-test-btn" onClick={sendTest} disabled={busy} variant="outline"><Send className="w-4 h-4 mr-1" /> Send Test SMS</Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3"><h3 className="font-head font-semibold text-slate-900">Delivery Log</h3><Button size="sm" variant="ghost" onClick={loadLogs}><RefreshCw className="w-3.5 h-3.5" /></Button></div>
            {logs.length === 0 ? <p className="text-sm text-slate-400">No SMS activity yet.</p> : (
              <div className="space-y-1.5" data-testid="sms-log-list">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-2">
                    <div className="min-w-0"><div className="text-slate-800 truncate">{l.to} <span className="text-slate-400">· {l.meta?.event || ""}</span></div><div className="text-xs text-slate-400 truncate">{l.message}{l.error ? ` · ${l.error}` : ""} · {new Date(l.created_at).toLocaleString()}</div></div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${LOG_CLS[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
